import React, { useEffect, useState } from 'react';
import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import { HelmetProvider } from 'react-helmet-async';
import { ScrollToTop } from './components/ScrollToTop';
import { supabase } from './lib/supabase';
import { getProfile } from './lib/profile';
import { WelcomePopup } from './components/WelcomePopup';
import { NewUserRedirect } from './components/NewUserRedirect';
import { startActivityTracking, stopActivityTracking } from './lib/activity';
import { useSessionRestoration } from './hooks/useSessionRestoration';
import Auth from './components/Auth';
import MainLayout from './components/Layout/MainLayout';
import { PageTransition } from './components/PageTransition';
import Cookies from 'js-cookie';
import { Toaster } from './components/ui/toaster';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProfileProvider } from './contexts/AuthProfileContext';

// Lazy load components for better performance
const Home = lazy(() => import('./pages/Home').then(module => ({ default: module.Home })));
const NewsFeed = lazy(() => import('./pages/NewsFeed').then(module => ({ default: module.NewsFeed })));
const Profile = lazy(() => import('./pages/Profile'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard').then(module => ({ default: module.AdminDashboard })));
const PostPage = lazy(() => import('./pages/posts/PostPage').then(module => ({ default: module.PostPage })));
const CommunityPostPage = lazy(() => import('./pages/posts/CommunityPostPage').then(module => ({ default: module.CommunityPostPage })));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess').then(module => ({ default: module.SubscriptionSuccess })));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Practitioners = lazy(() => import('./pages/Practitioners').then(module => ({ default: module.Practitioners })));
const PractitionerDetails = lazy(() => import('./pages/PractitionerDetails').then(module => ({ default: module.PractitionerDetails })));
const Events = lazy(() => import('./pages/Events').then(module => ({ default: module.Events })));
const CityEvents = lazy(() => import('./pages/CityEvents').then(module => ({ default: module.CityEvents })));
const EventDetails = lazy(() => import('./pages/EventDetails').then(module => ({ default: module.EventDetails })));
const Venues = lazy(() => import('./pages/Venues').then(module => ({ default: module.Venues })));
const VenueDetails = lazy(() => import('./pages/VenueDetails').then(module => ({ default: module.VenueDetails })));
const ClaimVenue = lazy(() => import('./pages/ClaimVenue').then(module => ({ default: module.ClaimVenue })));
const Jobs = lazy(() => import('./pages/Jobs').then(module => ({ default: module.Jobs })));
const JobDetails = lazy(() => import('./pages/JobDetails').then(module => ({ default: module.JobDetails })));
const Communities = lazy(() => import('./pages/Communities').then(module => ({ default: module.Communities })));
const CommunityDetails = lazy(() => import('./pages/CommunityDetails').then(module => ({ default: module.CommunityDetails })));
const Courses = lazy(() => import('./pages/Courses').then(module => ({ default: module.Courses })));
const CourseDetails = lazy(() => import('./pages/CourseDetails').then(module => ({ default: module.CourseDetails })));
const CourseLessons = lazy(() => import('./pages/CourseLessons'));
const Recipes = lazy(() => import('./pages/Recipes').then(module => ({ default: module.Recipes })));
const RecipeDetails = lazy(() => import('./pages/RecipeDetails').then(module => ({ default: module.RecipeDetails })));
const RecipeRedirect = lazy(() => import('./components/Recipes/RecipeRedirect').then(module => ({ default: module.RecipeRedirect })));
const Nutrition = lazy(() => import('./pages/tools/Nutrition').then(module => ({ default: module.Nutrition })));
// COMMENTED OUT: Astro Chart Tool
// const HoraryAstrology = lazy(() => import('./pages/tools/HoraryAstrology').then(module => ({ default: module.HoraryAstrology })));
const CorporateWellness = lazy(() => import('./pages/CorporateWellness').then(module => ({ default: module.CorporateWellness })));
const Host = lazy(() => import('./pages/Host').then(module => ({ default: module.Host })));
const Blogs = lazy(() => import('./pages/Blogs').then(module => ({ default: module.Blogs })));
const BlogPost = lazy(() => import('./pages/BlogPost').then(module => ({ default: module.BlogPost })));
const Articles = lazy(() => import('./pages/Articles').then(module => ({ default: module.Articles })));
const ArticleDetails = lazy(() => import('./pages/ArticleDetails').then(module => ({ default: module.ArticleDetails })));
const LiveStream = lazy(() => import('./pages/LiveStream').then(module => ({ default: module.LiveStream })));
const HostStream = lazy(() => import('./pages/HostStream').then(module => ({ default: module.HostStream })));
const Agriculture = lazy(() => import('./pages/Agriculture'));
const FieldDetails = lazy(() => import('./pages/FieldDetails').then(module => ({ default: module.FieldDetails })));
const Features = lazy(() => import('./pages/Features').then(module => ({ default: module.Features })));
const Tutorials = lazy(() => import('./pages/Tutorials').then(module => ({ default: module.Tutorials })));
const Chat = lazy(() => import('./pages/Chat'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(module => ({ default: module.TermsOfService })));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy').then(module => ({ default: module.CookiePolicy })));
const TicketTerms = lazy(() => import('./pages/TicketTerms').then(module => ({ default: module.TicketTerms })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(module => ({ default: module.ResetPassword })));
const Advertise = lazy(() => import('./pages/Advertise').then(module => ({ default: module.Advertise })));
const Notifications = lazy(() => import('./pages/Notifications').then(module => ({ default: module.Notifications })));
const AboutUs = lazy(() => import('./pages/AboutUs').then(module => ({ default: module.AboutUs })));
const Ambassador = lazy(() => import('./pages/Ambassador').then(module => ({ default: module.Ambassador })));
const Pricing = lazy(() => import('./pages/Pricing').then(module => ({ default: module.Pricing })));
const Suggestions = lazy(() => import('./pages/Suggestions').then(module => ({ default: module.Suggestions })));
const BookingOverview = lazy(() => import('./pages/BookingOverview').then(module => ({ default: module.BookingOverviewPage })));
const MyBookings = lazy(() => import('./pages/MyBookings').then(module => ({ default: module.MyBookings })));
const NearMe = lazy(() => import('./pages/NearMe'));
const Conversations = lazy(() => import('./pages/Conversations').then(module => ({ default: module.Conversations })));
const ConversationPostDetail = lazy(() => import('./pages/ConversationPostDetail').then(module => ({ default: module.ConversationPostDetail })));
const CheckIn = lazy(() => import('./pages/CheckIn'));

function AuthRequiredPage({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center">
      <div className="w-16 h-16 bg-accent-text/10 rounded-full flex items-center justify-center mb-6">
        <svg className="w-8 h-8 text-accent-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h1 className="text-2xl font-gelica font-bold mb-2">Sign in required</h1>
      <p className="text-content/60 mb-6 max-w-md">You need to be signed in to view this page.</p>
      <button
        onClick={onLogin}
        className="px-6 py-3 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors"
      >
        Sign In
      </button>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center">
      <h1 className="text-6xl font-gelica font-bold text-accent-text mb-4">404</h1>
      <h2 className="text-2xl font-gelica font-bold mb-2">Page not found</h2>
      <p className="text-content/60 mb-6 max-w-md">The page you are looking for does not exist or has been moved.</p>
      <a
        href="/"
        className="px-6 py-3 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors"
      >
        Go Home
      </a>
    </div>
  );
}

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize session restoration for iOS WebViews
  const { 
    isRestoring, 
    isIOSWebView, 
    sessionRestored, 
    error: restorationError,
    isAppRestart,
    appLevelRestoration
  } = useSessionRestoration();

  // Disable browser scroll restoration on app mount
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Extract OneSignal ID from URL and store in cookie
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const onesignalId = params.get('onesignal_push_id');
    
    if (onesignalId) {
      // Store in cookie that expires in 1 year
      Cookies.set('onesignal_id', onesignalId, { expires: 365 });
      
      // Remove the parameter from URL without reloading
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
    
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    const syncOneSignalId = async (userId: string) => {
      const onesignalId = Cookies.get('onesignal_id');
      if (!onesignalId) return;

      const { error } = await supabase
        .from('users')
        .update({ onesignal_id: onesignalId })
        .eq('id', userId);

      if (!error) {
        Cookies.remove('onesignal_id');
      }
    };

    // Check if user is already signed in
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        startActivityTracking();
        syncOneSignalId(session.user.id);

        // Note: New user redirect is now handled by NewUserRedirect component
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Note: New user redirect is now handled by NewUserRedirect component
      setSession(session);

      if (event === 'SIGNED_OUT') {
        stopActivityTracking();
        return;
      }

      if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        // Always close auth modal when user signs in or initial session restores.
        setShowAuth(false);
        startActivityTracking();
        // Try to get or create profile in background (non-blocking).
        getProfile(session.user.id).catch(() => {});
        syncOneSignalId(session.user.id);
      }
    });

    // Listen for custom event to show auth modal
    const handleShowAuth = () => setShowAuth(true);
    window.addEventListener('show-auth', handleShowAuth);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('show-auth', handleShowAuth);
    };
  }, []);

  // Loading fallback component
  const LoadingFallback = () => (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text"></div>
    </div>
  );

  // Enhanced loading component for iOS WebView session restoration
  const IOSWebViewLoadingFallback = () => (
    <div className="flex flex-col items-center justify-center min-h-screen space-y-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text"></div>
      {isRestoring && (
        <div className="text-center space-y-2">
          <p className="text-sm text-gray-600">
            {appLevelRestoration ? 'Restoring session after app restart...' : 'Restoring session...'}
          </p>
          {isAppRestart && (
            <p className="text-xs text-gray-500">
              App was restarted, recovering your session
            </p>
          )}
        </div>
      )}
      {sessionRestored && (
        <p className="text-sm text-green-600 text-center">
          {isAppRestart ? 'Session restored after app restart!' : 'Session restored successfully'}
        </p>
      )}
      {restorationError && (
        <div className="text-center space-y-2">
          <p className="text-sm text-red-600">
            Session restoration failed. Please try logging in again.
          </p>
          {isAppRestart && (
            <p className="text-xs text-gray-500">
              This can happen when the app was closed for a long time
            </p>
          )}
        </div>
      )}
    </div>
  );
  return (
    <ErrorBoundary>
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <Toaster />
        <div className="h-screen flex flex-col bg-background text-content">
          {showAuth && !session ? (
            <Auth onClose={() => setShowAuth(false)} />
          ) : null}

          {showWelcome && (
            <WelcomePopup onClose={() => setShowWelcome(false)} />
          )}

          <AuthProfileProvider session={session}>
            <MainLayout
              onLogin={() => setShowAuth(true)}
              isAuthenticated={!!session}
            >
              <NewUserRedirect />
              <Suspense fallback={isIOSWebView ? <IOSWebViewLoadingFallback /> : <LoadingFallback />}>
              <PageTransition>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={session ? <NewsFeed /> : <Home />} />
              <Route path="/practitioners" element={<Practitioners />} />
              <Route path="/practitioners/:slug" element={<PractitionerDetails />} />
              <Route path="/events" element={<Events />} />
              <Route path="/wellness-events-london" element={<CityEvents citySlug="london" />} />
              <Route path="/wellness-events-manchester" element={<CityEvents citySlug="manchester" />} />
              <Route path="/wellness-events-edinburgh" element={<CityEvents citySlug="edinburgh" />} />
              <Route path="/wellness-events-bristol" element={<CityEvents citySlug="bristol" />} />
              <Route path="/wellness-events-brighton" element={<CityEvents citySlug="brighton" />} />
              <Route path="/wellness-events-norwich" element={<CityEvents citySlug="norwich" />} />
              <Route path="/wellness-events-totnes" element={<CityEvents citySlug="totnes" />} />
              <Route path="/wellness-events-amsterdam" element={<CityEvents citySlug="amsterdam" />} />
              <Route path="/wellness-events-rotterdam" element={<CityEvents citySlug="rotterdam" />} />
              <Route path="/wellness-events-antwerp" element={<CityEvents citySlug="antwerp" />} />
              <Route path="/wellness-events-brussels" element={<CityEvents citySlug="brussels" />} />
              <Route path="/wellness-events-brugge" element={<CityEvents citySlug="brugge" />} />
              <Route path="/wellness-events-gent" element={<CityEvents citySlug="gent" />} />
              <Route path="/wellness-events-paris" element={<CityEvents citySlug="paris" />} />
              <Route path="/wellness-events-madrid" element={<CityEvents citySlug="madrid" />} />
              <Route path="/wellness-events-barcelona" element={<CityEvents citySlug="barcelona" />} />
              <Route path="/wellness-events-valencia" element={<CityEvents citySlug="valencia" />} />
              <Route path="/wellness-events-alicante" element={<CityEvents citySlug="alicante" />} />
              <Route path="/wellness-events-granada" element={<CityEvents citySlug="granada" />} />
              <Route path="/wellness-events-rome" element={<CityEvents citySlug="rome" />} />
              <Route path="/events/:slug" element={<EventDetails />} />
              <Route path="/check-in/:token" element={<CheckIn />} />
              <Route path="/venues" element={<Venues />} />
              <Route path="/venues/:slug" element={<VenueDetails />} />
              <Route path="/claim/:token" element={<ClaimVenue />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/jobs/:slug" element={<JobDetails />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:slug" element={<CourseDetails />} />
              <Route path="/recipes" element={<Recipes />} />
              <Route path="/recipes/id/:id" element={<RecipeRedirect />} />
              <Route path="/recipes/:slug" element={<RecipeDetails />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/articles/:slug" element={<ArticleDetails />} />
              <Route path="/blogs" element={<Blogs />} />
              <Route path="/blogs/:slug" element={<BlogPost />} />
              <Route path="/corporate-wellness" element={<CorporateWellness />} />
              <Route path="/host" element={<Host />} />
              <Route path="/tools/nutrition" element={<Nutrition />} />
              {/* COMMENTED OUT: Astro Chart Tool */}
              {/* <Route path="/tools/horary-astrology" element={<HoraryAstrology />} /> */}
              <Route path="/chat" element={<Chat />} />
              <Route path="/gardens" element={<Agriculture />} />
              <Route path="/gardens/:id" element={<FieldDetails />} />
              {/* Legacy route redirect */}
              <Route path="/agriculture" element={<Agriculture />} />
              <Route path="/agriculture/:id" element={<FieldDetails />} />
              <Route path="/chat/:userId" element={<Chat />} />
              <Route path="/communities" element={<Communities />} />
              <Route path="/conversations" element={<Conversations />} />
              <Route path="/conversations/post/:slugOrId" element={<ConversationPostDetail />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/ticket-terms" element={<TicketTerms />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/advertise" element={<Advertise />} />
              <Route path="/features" element={<Features />} />
              <Route path="/tutorials" element={<Tutorials />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/ambassador" element={<Ambassador />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/live-stream" element={<LiveStream />} />
              <Route path="/live-stream/host" element={<HostStream />} />
              <Route path="/contact" element={<Suggestions />} />
              <Route path="/bookings/:practitionerId" element={<BookingOverview />} />
              <Route path="/my-bookings" element={<MyBookings />} />
              <Route path="/near-me" element={<NearMe />} />

              {/* Protected Routes - Require Authentication */}
              {session ? (
                <>
                  <Route path="/onboarding" element={<Onboarding />} />
                  <Route path="/communities/:id/*" element={<CommunityDetails />} />
                  <Route path="/communities/posts/:id" element={<CommunityPostPage />} />
                  <Route path="/posts/:id" element={<PostPage />} />
                  <Route path="/courses/:slug/learn" element={<CourseLessons />} />
                  <Route path="/profile/:username/*" element={<Profile />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                </>
              ) : (
                <>
                  {/* Show auth prompt for protected routes when logged out */}
                  <Route path="/communities/:id/*" element={<AuthRequiredPage onLogin={() => setShowAuth(true)} />} />
                  <Route path="/communities/posts/:id" element={<AuthRequiredPage onLogin={() => setShowAuth(true)} />} />
                  <Route path="/posts/:id" element={<AuthRequiredPage onLogin={() => setShowAuth(true)} />} />
                  <Route path="/courses/:slug/learn" element={<AuthRequiredPage onLogin={() => setShowAuth(true)} />} />
                  <Route path="/profile/:username/*" element={<AuthRequiredPage onLogin={() => setShowAuth(true)} />} />
                  <Route path="/admin" element={<AuthRequiredPage onLogin={() => setShowAuth(true)} />} />
                </>
              )}

              {/* 404 catch-all */}
              <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </PageTransition>
              </Suspense>
            </MainLayout>
          </AuthProfileProvider>
        </div>
      </Router>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App